import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import * as yup from 'yup'

import { useGetArisCompanyConfigQuery, useUpsertArisConfigMutation } from '../../app/apiSlice'
import { AdminPage, AdminPageHeader, AdminSection } from '../../common/components'
import { Button, InputField, Text } from '../../common/designs'

const formSchema = yup
  .object({
    tenant_url: yup.string().required('Tenant URL is required'),
    client_id: yup.string().required('Client ID is required'),
    client_secret: yup.string().required('Client Secret is required'),
  })
  .required()

type Inputs = {
  tenant_url: string
  client_id: string
  client_secret: string
}

const ArisIntegration = () => {
  const [upsertArisConfig, { isLoading }] = useUpsertArisConfigMutation()

  // Fetch existing configuration
  const { data: configData, isLoading: isLoadingConfig } = useGetArisCompanyConfigQuery()

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<Inputs>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      tenant_url: '',
      client_id: '',
      client_secret: '',
    },
  })

  // Populate form with existing configuration when data loads
  useEffect(() => {
    if (configData) {
      const { tenant_url, client_id, client_secret } = configData
      reset({ tenant_url, client_id, client_secret })
    }
  }, [configData, reset])

  const onSave: SubmitHandler<Inputs> = async ({ tenant_url, client_id, client_secret }) => {
    try {
      await upsertArisConfig({
        tenantUrl: tenant_url,
        clientId: client_id,
        clientSecret: client_secret,
      }).unwrap()

      toast.success('ARIS Integration settings saved successfully')
    } catch (error) {
      console.error('Failed to save ARIS configuration:', error)
      toast.error('Failed to save ARIS Integration settings')
    }
  }

  return (
    <AdminPage width='wide'>
      <AdminPageHeader
        title='ARIS Integration'
        description='Configure your ARIS tenant credentials used to synchronize integration data.'
      />

      {isLoadingConfig ? (
        <Text size='m' className='mt-8 text-gray-600'>
          Loading configuration...
        </Text>
      ) : (
        <AdminSection className='mt-8'>
          <form onSubmit={handleSubmit(onSave)} className='flex flex-col gap-4'>
            <div>
              <Controller
                name='tenant_url'
                control={control}
                render={({ field }) => (
                  <InputField
                    label='Tenant URL'
                    type='url'
                    error={errors.tenant_url?.message}
                    className='w-96'
                    placeholder='https://processmaker.aris.com'
                    {...field}
                  />
                )}
              />
            </div>

            <div>
              <Controller
                name='client_id'
                control={control}
                render={({ field }) => (
                  <InputField
                    label='Client ID'
                    type='text'
                    error={errors.client_id?.message}
                    className='w-96'
                    placeholder='xxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
                    {...field}
                  />
                )}
              />
            </div>

            <div>
              <Controller
                name='client_secret'
                control={control}
                render={({ field }) => (
                  <InputField
                    label='Client Secret'
                    type='password'
                    error={errors.client_secret?.message}
                    className='w-96'
                    placeholder='xxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
                    {...field}
                  />
                )}
              />
            </div>

            <Button type='submit' className='w-96' disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </form>
        </AdminSection>
      )}
    </AdminPage>
  )
}

export default ArisIntegration

